import { CheckCircle, Close } from "@mui/icons-material";
import { Modal } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { getBidHistoryArchive } from "../../../Services/ReverseAuction/GetApi";
import Styles from "./RFPBidHistory.module.css";
import { useSelector } from "react-redux";

function formatDateTime(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

const PriceHistoryTable = ({ entries, showEntityName }) => (
  <div className={Styles.bidSection}>
    <div className={Styles.priceHistoryWrap}>
      <div
        className={`display_flex_row tableHeading_type2 ${Styles.priceHistorySubHead}`}
      >
        <div style={{ width: "8%" }}>S.No</div>
        <div style={{ width: showEntityName ? "20%" : "26%" }}>
          Price (₹/kWh)
        </div>
        <div style={{ width: showEntityName ? "20%" : "26%" }}>
          Quantity (MW)
        </div>
        <div style={{ width: showEntityName ? "24%" : "32%" }}>Updated At</div>
        {showEntityName && (
          <div style={{ width: "20%" }}>Bidder Entity Name</div>
        )}
        <div style={{ width: "8%" }} />
      </div>

      {entries.length > 0 ? (
        entries.map((entry, i) => {
          // Highlight the final bid, but not when the winner bid is rejected.
          const isHighlighted =
            entry.isFinal && entry.winnerBidStatus !== "REJECTED";
          return (
            <div
              key={entry.id ?? i}
              className={`display_flex_row tableDetails ${Styles.priceHistoryRow} ${isHighlighted ? Styles.finalRow : ""}`}
              style={isHighlighted ? { fontWeight: "bold" } : {}}
            >
              <div style={{ width: "8%" }}>{i + 1}</div>
              <div
                style={{ width: showEntityName ? "20%" : "26%" }}
                className={Styles.priceCell}
              >
                {entry.bidPrice}
              </div>
              <div
                className={Styles.quantityCell}
                style={{ width: showEntityName ? "20%" : "26%" }}
              >
                {entry.bidQuantityMw}
              </div>
              <div style={{ width: showEntityName ? "24%" : "32%" }}>
                {formatDateTime(entry.createdAt)}
              </div>
              {showEntityName && (
                <div style={{ width: "20%" }}>
                  {entry.bidderEntityName ?? "-"}
                </div>
              )}
              <div
                style={{
                  width: "8%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isHighlighted && (
                  <CheckCircle
                    style={{ fontSize: 14, color: "var(--primary-color-text)" }}
                  />
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className={`display_flex_row_center ${Styles.noHistory}`}>
          No price revisions
        </div>
      )}
    </div>
  </div>
);

// Bid history for the RFP / RFQ (closed-bid) flow. Unlike RABidHistory — which
// buckets bids into IPO/RA auction stages and, for consumers, shows only the RA
// stage — the RFP flow has a single closed-bid stage. This component therefore
// flattens every returned bid's priceHistory into one chronological table with
// no stage tabs, so CB-stage data is always shown.
const RFPBidHistory = ({
  open,
  handleClose,
  tenderId,
  requisitionId,
  showEntityName = false,
}) => {
  const entityData = useSelector(
    (state) => state?.persistedReducer?.entityDetails,
  );
  const role = useSelector((state) => state?.persistedReducer?.userType);
  const activeEntityId = entityData?.consumerId || entityData?.generatorId;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !tenderId || !requisitionId) return;
    let mounted = true;
    setLoading(true);
    setData([]);
    getBidHistoryArchive({ tenderId, requisitionId })
      .then((res) => {
        if (!mounted) return;
        const raw = res?.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        setData(list);
      })
      .catch(() => {
        if (mounted) setData([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, tenderId, requisitionId]);

  const filteredData = useMemo(() => {
    if (!data.length) return [];
    return data.filter((bid) => {
      if (role === "CONSUMER") {
        return (
          bid.consumerId === activeEntityId ||
          (bid.consumerId == null && bid.generatorId != null)
        );
      }
      if (role === "GENERATOR") {
        return (
          bid.generatorId === activeEntityId ||
          (bid.generatorId == null && bid.consumerId != null)
        );
      }
      return true;
    });
  }, [data, role, activeEntityId]);

  // Flatten all priceHistory entries across bids, sorted by createdAt. When a
  // bid has no priceHistory, fall back to the bid's own placed values so a
  // single placed bid still appears.
  const entries = useMemo(() => {
    if (!filteredData.length) return [];
    const flat = [];
    filteredData.forEach((bid) => {
      if (bid.priceHistory?.length > 0) {
        bid.priceHistory.forEach((entry) =>
          flat.push({
            ...entry,
            bidderEntityName: bid.bidderEntityName ?? "-",
            winnerBidStatus: bid.winnerBidStatus ?? null,
          }),
        );
      } else if (bid.bidStatus !== "REJECTED") {
        flat.push({
          id: bid.connectId,
          bidPrice: bid.bidPrice,
          bidQuantityMw: bid.bidQuantityMw,
          createdAt: bid.lastUpdatedAt ?? bid.placedAt,
          bidderEntityName: bid.bidderEntityName ?? "-",
          winnerBidStatus: bid.winnerBidStatus ?? null,
          isFinal: true,
        });
      }
    });
    flat.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return flat;
  }, [filteredData]);

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={`popupModal ${Styles.modalContainer}`}>
        {/* Header */}
        <div className={`display_flex_row modalHeading`}>
          <div className="headingText">Bid History</div>
          <Close
            style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
            onClick={handleClose}
          />
        </div>

        {/* Stage label — the RFP flow has a single closed-bid (IPO) stage, so
            show a static IPO chip rather than the RA-style clickable tabs. */}
        <div className={Styles.tabRow}>
          <div className={Styles.tabBar}>
            <div className={`${Styles.tabBtn} ${Styles.tabBtnActive}`}>IPO</div>
          </div>
        </div>

        {/* Body */}
        <div className={Styles.modalBody}>
          {loading && (
            <div className={Styles.skeletonWrap}>
              <div className={Styles.skeletonHeader}>
                {[8, 22, 22, 28, 12, 8].map((w, i) => (
                  <div
                    key={i}
                    className={Styles.skeletonCell}
                    style={{ width: `${w}%` }}
                  />
                ))}
              </div>
              {[1, 2, 3].map((r) => (
                <div key={r} className={Styles.skeletonRow}>
                  {[8, 22, 22, 28, 12, 8].map((w, i) => (
                    <div
                      key={i}
                      className={Styles.skeletonCell}
                      style={{ width: `${w}%`, animationDelay: `${r * 0.1}s` }}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
          {!loading && (
            <PriceHistoryTable
              entries={entries}
              showEntityName={showEntityName}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RFPBidHistory;
